/**
 * QR matrices for the programme zones.
 *
 * A wall panel is the one place a QR code genuinely earns its keep: a visitor
 * standing in front of a door panel cannot type a ticket URL, and the panel has
 * no input. So `performance-detail` can offer the production's ticket link and
 * the performance's surtitle-app link as codes (task 16.5).
 *
 * Encoding is delegated to `qrcode-generator` — a dependency-free, long-standing
 * implementation of the ISO/IEC 18004 encoder. Hand-rolling Reed-Solomon and
 * mask selection here would be a few hundred lines whose only oracle is a phone
 * camera; a wrong mask still *looks* like a QR code on a wall, which is exactly
 * the failure that would ship unnoticed.
 *
 * What this module owns is everything downstream of the encoder: a plain module
 * matrix, an SVG path, and the decision that an unencodable value renders no code
 * rather than throwing inside a zone.
 */
import qrcode from 'qrcode-generator';

/** Modules of quiet zone around the symbol. Four is what ISO/IEC 18004 requires. */
export const QR_QUIET_ZONE = 4;

export interface QrMatrix {
  /** Modules per side, excluding the quiet zone. */
  size: number;
  /** `modules[row][col]`, true where the module is dark. */
  modules: boolean[][];
}

/**
 * ASCII the encoder can take at face value.
 *
 * `qrcode-generator`'s default byte encoder truncates each character to one
 * byte, so a URL carrying an umlaut would encode to mojibake. Percent-encoding
 * first keeps the payload ASCII and leaves an already-encoded URL alone —
 * `encodeURI` does not touch `%`.
 */
function asciiPayload(value: string): string {
  return /^[\x20-\x7e]*$/.test(value) ? value : encodeURI(value);
}

/**
 * The module matrix for a value, or `null` when there is nothing to encode.
 *
 * Error correction level M (~15% recoverable) is the usual choice for print and
 * signage: L is fragile behind glass with a reflection across it, and Q/H spend
 * modules a phone at arm's length then has to resolve.
 *
 * A value too long for a version-40 symbol, or an encoder that throws for any
 * other reason, yields `null`. A zone renders the rest of the performance
 * without a code; the alternative is an exception thrown during a render, which
 * on a wall panel means a blank wall.
 */
export function qrMatrix(value: string, ecc: 'L' | 'M' | 'Q' | 'H' = 'M'): QrMatrix | null {
  const payload = asciiPayload(value.trim());
  if (payload.length === 0) return null;
  try {
    // Type number 0 lets the encoder pick the smallest version that fits.
    const qr = qrcode(0, ecc);
    qr.addData(payload, 'Byte');
    qr.make();
    const size = qr.getModuleCount();
    const modules: boolean[][] = [];
    for (let row = 0; row < size; row += 1) {
      const line: boolean[] = [];
      for (let col = 0; col < size; col += 1) line.push(qr.isDark(row, col));
      modules.push(line);
    }
    return { size, modules };
  } catch {
    return null;
  }
}

/**
 * One SVG path covering every dark module, in a coordinate system of one unit
 * per module with the quiet zone already offset.
 *
 * A single path rather than a rect per module: a version-10 symbol has ~1400
 * dark modules, and 1400 DOM nodes per zone is a real cost on a Pi that also
 * has video to decode.
 */
export function qrSvgPath(matrix: QrMatrix, quietZone: number = QR_QUIET_ZONE): string {
  const parts: string[] = [];
  for (let row = 0; row < matrix.size; row += 1) {
    const line = matrix.modules[row];
    if (line === undefined) continue;
    let col = 0;
    while (col < matrix.size) {
      if (line[col] !== true) {
        col += 1;
        continue;
      }
      // Runs, not single modules: horizontally adjacent dark modules become one
      // rectangle, which roughly halves the path for a typical symbol.
      let run = 1;
      while (col + run < matrix.size && line[col + run] === true) run += 1;
      parts.push(`M${col + quietZone},${row + quietZone}h${run}v1h-${run}z`);
      col += run;
    }
  }
  return parts.join('');
}

/** Side of the rendered viewBox, in modules, including both quiet zones. */
export function qrViewBoxSize(matrix: QrMatrix, quietZone: number = QR_QUIET_ZONE): number {
  return matrix.size + quietZone * 2;
}
